import * as React from 'react';

interface InfiniteScrollSentinelProps {
  /** Called when the sentinel scrolls into view. Wire to the data source's loadMore. */
  onVisible: () => void;
  /** When false, the observer is not attached (e.g. no more pages / mid-load). */
  enabled: boolean;
  /** Scroll container the sentinel lives in; observer roots here (null = viewport). */
  root?: React.RefObject<HTMLElement>;
}

/**
 * A zero-height marker at the bottom of the account list. When it intersects the
 * scroll container, `onVisible` fires — the flat-list uses this to auto-load the
 * next page instead of a "Show 30 more" button. Disabled (`enabled={false}`)
 * when there is nothing more to load or a fetch is already in flight, so the
 * observer never fires a redundant page request.
 */
export const InfiniteScrollSentinel: React.VFC<InfiniteScrollSentinelProps> = ({
  onVisible,
  enabled,
  root,
}) => {
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  // Keep the latest callback without re-creating the observer each render.
  const onVisibleRef = React.useRef(onVisible);
  onVisibleRef.current = onVisible;

  React.useEffect(() => {
    const node = sentinelRef.current;
    if (!enabled || !node || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          onVisibleRef.current();
        }
      },
      { root: root?.current ?? null, rootMargin: '0px 0px 200px 0px' },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, root]);

  return (
    <div ref={sentinelRef} data-testid="account-infinite-scroll-sentinel" />
  );
};
